import React, { useMemo, useState } from 'react'
import {
  ActionButtons,
  Badge,
  DataTable,
  DataTableColumn,
  type DataTableSortDirection,
  EmptyState,
} from '@enamelai/design-system'
import { Icon as UserAvatar } from '@enamelai/design-system/icons/User_avatar'
import { TableSkeleton } from '@components/ui'
import type { AiReceptionistPatientRow } from '@services/api/ai-receptionist-settings.api'
import { PATIENT_STATUS_BADGE_COLOR } from '@constants/PatientManagement/status'
import { AiReceptionistIcon } from '@components/common'
import { StatusTypes } from '@components/common/StatusBadge/StatusBadge.component'

export type AiReceptionistPatientsTabProps = {
  loading: boolean
  patients: AiReceptionistPatientRow[]
  onView: (patientId: string) => void
  onEdit: (patientId: string) => void
  onDelete: (patientId: string) => void
  onPhone: (patientId: string) => void
  onMail: (patientId: string) => void
  onMessage: (patientId: string) => void
  emptyDescription?: string
}

const AiReceptionistPatientsTab = ({
  loading,
  patients,
  onView,
  onEdit,
  onDelete,
  onPhone,
  onMail,
  onMessage,
  emptyDescription,
}: AiReceptionistPatientsTabProps): React.ReactElement => {
  // Patients start sorted A-Z by name.
  const [sortColumnId, setSortColumnId] = useState<string>('patient_name')
  const [sortDirection, setSortDirection] =
    useState<DataTableSortDirection>('asc')

  const sortedPatients = useMemo(() => {
    const direction = sortDirection === 'asc' ? 1 : -1
    return [...patients].sort((left, right) => {
      const leftValue = String(
        (left as Record<string, unknown>)[sortColumnId] ?? '',
      )
      const rightValue = String(
        (right as Record<string, unknown>)[sortColumnId] ?? '',
      )
      return (
        leftValue.localeCompare(rightValue, undefined, {
          sensitivity: 'base',
        }) * direction
      )
    })
  }, [patients, sortColumnId, sortDirection])

  const handleSort = (columnId: string): void => {
    if (columnId === sortColumnId) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'))
      return
    }
    setSortColumnId(columnId)
    setSortDirection('asc')
  }
  const renderActions = (row: AiReceptionistPatientRow): React.ReactElement => {
    const patientId = row.patient_id as string
    const email = row.email as string | null
    return (
      <ActionButtons
        onView={() => onView(patientId)}
        onEdit={() => onEdit(patientId)}
        onDelete={() => onDelete(patientId)}
        onPhone={() => onPhone(patientId)}
        onMail={email ? () => onMail(patientId) : undefined}
        onMessage={() => onMessage(patientId)}
      />
    )
  }

  const columns: DataTableColumn[] = [
    {
      id: 'patient_name',
      label: 'Patient name',
      sortable: true,
      renderCell: (row) => (
        <span className="ai-receptionist-name">
          <span className="ai-receptionist-name__text">
            {String(row.patient_name ?? '')}
          </span>
          <AiReceptionistIcon label="Created by AI receptionist" />
        </span>
      ),
    },
    {
      id: 'status',
      label: 'Status',
      sortable: true,
      renderCell: (row) => {
        const status = row.status as string
        return (
          <Badge
            label={status}
            mode="tinted"
            color={PATIENT_STATUS_BADGE_COLOR[status as StatusTypes] ?? 'gray'}
          />
        )
      },
    },
  ]

  if (loading) {
    return (
      <TableSkeleton
        className="ai-receptionist-patients__table"
        columns={[
          { width: '40%', kind: 'text' },
          { width: '20%', kind: 'badge' },
          { width: '180px', kind: 'actions' },
        ]}
        rows={3}
        showToolbar={false}
        showPagination={false}
        actionCount={3}
        fillHeight={false}
      />
    )
  }

  if (patients.length === 0) {
    return (
      <EmptyState
        className="ai-receptionist-patients__empty"
        illustration={<UserAvatar className="empty-icon" aria-hidden />}
        title="No patients found"
        description={
          emptyDescription ??
          'No patients have been created by the AI receptionist yet.'
        }
      />
    )
  }

  return (
    <DataTable
      className="ai-receptionist-patients__table"
      columns={columns}
      rows={sortedPatients.map((patient) => ({
        id: patient.patient_id,
        ...patient,
      }))}
      sortColumnId={sortColumnId}
      sortDirection={sortDirection}
      onSort={handleSort}
      renderActions={renderActions}
    />
  )
}

export default AiReceptionistPatientsTab
